import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Plus, Search, Mail, Phone, MapPin, Pencil, Trash2, Building, Linkedin } from 'lucide-react';
import { CopyEmail } from '@/components/CopyEmail';
import { toast } from 'sonner';
import { CountrySelect } from '@/components/CountrySelect';
import { Pagination } from '@/components/Pagination';

const API = '/api';

const emptyForm = {
  name: '', email: '', phone: '', designation: '', company_name: '',
  location: '', website: '',
};

export default function BiotechContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sourceCounts, setSourceCounts] = useState({ manual: 0, database: 0, all: 0 });
  const searchTimeoutRef = useRef(null);
  const [sourceFilter, setSourceFilter] = useState('manual');
  const [companyFilter, setCompanyFilter] = useState('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [biotechCompanies, setBiotechCompanies] = useState([]);
  const [companySuggestOpen, setCompanySuggestOpen] = useState(false);

  const fetchContacts = async (page = currentPage, searchQuery = search, sourceVal = sourceFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5000 });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (sourceVal && sourceVal !== 'all') params.set('source', sourceVal);
      const [contactsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/biotech-contacts?${params}`, { withCredentials: true }),
        axios.get(`${API}/biotech-companies?limit=5000`, { withCredentials: true }),
      ]);
      const data = contactsRes.data;
      setContacts(data.items || data);
      setTotalPages(data.pages || 1);
      setTotalCount(data.total || 0);
      setCurrentPage(data.page || 1);
      if (data.source_counts) setSourceCounts(data.source_counts);
      setBiotechCompanies(companiesRes.data?.items || companiesRes.data || []);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchContacts(1, '', sourceFilter); }, []);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(1);
      fetchContacts(1, value, sourceFilter);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchContacts(newPage, search, sourceFilter);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSourceFilterChange = (newFilter) => {
    setSourceFilter(newFilter);
    setCurrentPage(1);
    fetchContacts(1, search, newFilter);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/biotech-contacts/${editing}`, form, { withCredentials: true });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/biotech-contacts`, form, { withCredentials: true });
        toast.success('Contact added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchContacts();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || 'This contact already exists');
      } else {
        toast.error('Failed to save contact');
      }
    }
  };

  const handleEdit = (contact) => {
    setForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      designation: contact.designation || '',
      company_name: contact.company_name || '',
      location: contact.location || '',
      website: contact.website || '',
    });
    setEditing(contact.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/biotech-contacts/${id}`, { withCredentials: true });
      toast.success('Contact deleted');
      setDeleteTarget(null);
      fetchContacts();
    } catch { toast.error('Failed to delete'); setDeleteTarget(null); }
  };

  const getCompanySuggestions = () => {
    if (!form.company_name || form.company_name.length < 2) return [];
    const term = form.company_name.toLowerCase();
    return biotechCompanies.filter(c =>
      c.company_name?.toLowerCase().includes(term)
    ).slice(0, 6);
  };

  const selectCompanySuggestion = (company) => {
    setForm(p => ({
      ...p,
      company_name: company.company_name || '',
      website: p.website || company.website || '',
      location: p.location || company.location || '',
    }));
    setCompanySuggestOpen(false);
  };

  const uniqueCompanies = [...new Set(contacts.map(c => c.company_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  // Build company source map for filtering
  const companySourceMap = {};
  biotechCompanies.forEach(c => { companySourceMap[(c.company_name || '').toLowerCase()] = c.source || 'manual'; });

  const filtered = contacts.filter(c => {
    return (companyFilter === 'All' || c.company_name === companyFilter);
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="biotech-contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Biotech Contacts
          </h1>
          <p className="text-sm text-slate-500 mt-1">Individual contacts from the biotech industry</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ ...emptyForm }); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md shadow-sm active:scale-95 transition-all" data-testid="add-biotech-contact-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-visible">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? 'Edit Contact' : 'Add Biotech Contact'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="input-biotech-contact-name" />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. CEO, CSO, VP R&D" data-testid="input-biotech-contact-designation" />
                </div>
                <div className="space-y-2 relative">
                  <Label>Company Name</Label>
                  <Input value={form.company_name} onChange={e => { setForm(p => ({ ...p, company_name: e.target.value })); setCompanySuggestOpen(true); }} onFocus={() => setCompanySuggestOpen(true)} onBlur={() => setTimeout(() => setCompanySuggestOpen(false), 250)} placeholder="Biotech company name" autoComplete="off" data-testid="input-biotech-contact-company" />
                  {companySuggestOpen && getCompanySuggestions().length > 0 && (
                    <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="company-suggestions">
                      {getCompanySuggestions().map((c, i) => (
                        <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); selectCompanySuggestion(c); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 cursor-pointer">
                          <p className="text-sm font-medium text-slate-800">{c.company_name}</p>
                          {c.location && <p className="text-xs text-slate-500">{c.location}</p>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="input-biotech-contact-email" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="input-biotech-contact-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <CountrySelect value={form.location} onChange={v => setForm(p => ({ ...p, location: v }))} testId="input-biotech-contact-location" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> LinkedIn URL</Label>
                <Input
                  value={form.linkedin_url || ''}
                  onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                  placeholder="https://www.linkedin.com/in/…"
                  data-testid="input-biotech-contact-linkedin"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input type="url" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://company-website.com" data-testid="input-biotech-contact-website" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-biotech-contact-btn">{editing ? 'Update' : 'Add Contact'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex bg-slate-100 rounded-md p-0.5 mr-1" data-testid="contacts-source-filter">
          <button type="button" onClick={() => handleSourceFilterChange('manual')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="contacts-filter-my-clients">My Companies ({sourceCounts.manual})</button>
          <button type="button" onClick={() => handleSourceFilterChange('bio_international')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'bio_international' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="contacts-filter-database">Database ({sourceCounts.database})</button>
          <button type="button" onClick={() => handleSourceFilterChange('all')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="contacts-filter-all">All ({sourceCounts.all})</button>
        </div>
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search by name, company, location, email..." value={searchInput} onChange={e => handleSearchChange(e.target.value)} data-testid="search-biotech-contacts" />
        </div>
        {uniqueCompanies.length > 0 && (
          <select
            value={companyFilter}
            onChange={e => { setCompanyFilter(e.target.value); setCurrentPage(1); }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"
            data-testid="company-filter-biotech-contacts"
          >
            <option value="All">All Companies</option>
            {uniqueCompanies.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">{search || companyFilter !== 'All' ? 'No contacts match your search' : 'No biotech contacts yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="biotech-contacts-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Name</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Designation</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Contact</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => {
                  const company = c.company_name ? biotechCompanies.find(bc => bc.company_name === c.company_name) : null;
                  return (
                  <TableRow key={c.id} className="hover:bg-slate-50/50" data-testid={`biotech-contact-row-${c.id}`}>
                    <TableCell className="py-2">
                      <p className="text-xs font-semibold text-slate-900">{c.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {c.company_name && (
                          <div className="flex items-center gap-1">
                            <Building className="w-2.5 h-2.5 text-slate-400" />
                            {company ? (
                              <a href={`/biotech/${company.id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline" data-testid={`company-link-${c.id}`}>{c.company_name}</a>
                            ) : (
                              <span className="text-[10px] text-slate-500">{c.company_name}</span>
                            )}
                          </div>
                        )}
                        {c.location && (
                          <div className="flex items-center gap-1 text-[10px] text-slate-400">
                            <MapPin className="w-2.5 h-2.5" />{c.location}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-[11px] text-slate-600">{c.designation || '—'}</TableCell>
                    <TableCell className="py-2">
                      <div className="space-y-0.5">
                        {c.email && <div className="flex items-center gap-1 text-[11px] text-slate-500"><Mail className="w-2.5 h-2.5" />{c.email}<CopyEmail email={c.email} /></div>}
                        {c.phone && <div className="flex items-center gap-1 text-[11px] text-slate-500"><Phone className="w-2.5 h-2.5" />{c.phone}</div>}
                        {!c.email && !c.phone && <span className="text-[10px] text-slate-400">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right py-2">
                      <div className="flex items-center justify-end gap-0">
                        {c.linkedin_url && (
                          <a
                            href={c.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#0A66C2] hover:bg-sky-50"
                            title="Open LinkedIn profile"
                            data-testid={`linkedin-biotech-contact-${c.id}`}
                          >
                            <Linkedin className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleEdit(c); }} data-testid={`edit-biotech-contact-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }} data-testid={`delete-biotech-contact-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
        <Pagination page={currentPage} pages={totalPages} total={totalCount} onPageChange={handlePageChange} />
        </>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget?.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-biotech-contact-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-biotech-contact-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
